import { ArrowRight, Check, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { cacheHitPercent, formatFee, type PublicPlans } from "@/hooks/queries/usePublicPlans";
import { SectionHeading, SECTION_CLASS } from "./SectionHeading";

const pct = (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

const PLAN_TAGLINES: Record<string, string> = {
  payg: "Pay as you go. Build and ship your own apps.",
  pro: "Lower fees for teams with steady traffic.",
  agency: "Resell your apps to your clients.",
  agency_scale: "For agencies with a larger client book.",
};

interface PricingProps {
  data: PublicPlans;
  onStart: () => void;
  startLabel: string;
}

export function Pricing({ data, onStart, startLabel }: PricingProps) {
  const { plans, fees } = data;
  const byokRates = plans.map((p) => p.features.byokFeePercent);
  const byokMin = byokRates.length ? Math.min(...byokRates) : fees.byokFeePercentDefault;
  const byokMax = byokRates.length ? Math.max(...byokRates) : fees.byokFeePercentDefault;
  const cachePct = cacheHitPercent(fees);

  const usage = [
    {
      title: "Your own provider keys",
      body: `${byokMin === byokMax ? pct(byokMin) : `${pct(byokMin)}–${pct(byokMax)}`} of list price, depending on your plan. The provider bills you for the tokens directly.`,
    },
    ...(fees.platformKeysEnabled
      ? [{ title: "Our provider keys", body: `The model's list price + ${pct(fees.platformMarkupPercent)}. Nothing to set up.` }]
      : []),
    {
      title: "Semantic cache hits",
      body: cachePct === 0 ? "Free." : cachePct >= 100 ? "Charged at the normal price." : `${pct(cachePct)} of the normal price.`,
    },
    {
      title: "Hosted runs",
      body: `RAG query ${formatFee(fees.ragQueryFee)}, agent run ${formatFee(fees.agentRunFee)}, workflow run ${formatFee(fees.workflowRunFee)}, before your plan's discount. Model usage inside a run is charged as above.`,
    },
    {
      title: "Card top-ups",
      body: `${pct(fees.topUpFeePercent)} + ${formatFee(fees.topUpFeeFixed)} processing fee per top-up, minimum ${formatFee(fees.topUpMin)}.`,
    },
  ];

  return (
    <section id="pricing" className={SECTION_CLASS}>
      <SectionHeading
        eyebrow="Pricing"
        title="Prepaid. No bill shock."
        accent="Start free, upgrade when you resell."
        description="Every plan runs on a prepaid wallet. Subscriptions are paid from wallet credit; downgrades take effect at the end of the billing period."
        className="mb-12 sm:mb-16"
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan) => {
          const f = plan.features;
          const highlighted = plan.name === "agency";
          const lines = [
            `Own-key platform fee: ${pct(f.byokFeePercent)} of list price`,
            f.appFeeDiscountPercent > 0 ? `${pct(f.appFeeDiscountPercent)} off hosted-run fees` : "Standard hosted-run fees",
            f.maxClients > 0 ? `Up to ${f.maxClients} client accounts` : "Single account, no client accounts",
          ];
          return (
            <div key={plan.name} className={cn("rounded-card p-6 flex flex-col", highlighted ? "bg-brand-soft" : "bg-card")}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-medium">{plan.displayName}</h3>
                {highlighted && (
                  <span className="inline-flex items-center h-6 rounded-full bg-card px-2.5 text-[11px] font-medium">For agencies</span>
                )}
              </div>
              <p className="text-[13px] text-muted-foreground mt-1 min-h-[2.5em]">{PLAN_TAGLINES[plan.name] ?? ""}</p>
              <p className="mt-6">
                <span className="text-[40px] leading-none font-medium tracking-tight tabular">{formatFee(plan.monthlyPrice)}</span>
                <span className="text-[13px] text-muted-foreground ml-1">/ month</span>
              </p>
              <ul className="mt-6 mb-8 grid gap-2.5">
                {lines.map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-[14px]">
                    <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-brand text-brand-foreground flex items-center justify-center">
                      <Check className="h-2.5 w-2.5" strokeWidth={2.6} />
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
              <Button
                variant={highlighted ? "default" : "outline"}
                className="mt-auto w-full rounded-full h-11"
                onClick={onStart}
              >
                {plan.monthlyPrice === 0 ? startLabel : `Choose ${plan.displayName}`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Enterprise */}
      <div className="mt-4 rounded-card bg-card p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium">Enterprise</h3>
          <p className="text-[15px] text-muted-foreground mt-1">Custom volume pricing and onboarding.</p>
        </div>
        <Button variant="outline" className="rounded-full h-11 px-6 gap-2 w-full md:w-auto" asChild>
          <a href="mailto:sales@genaicore.com">
            <Mail className="h-4 w-4" strokeWidth={1.7} />
            Contact sales
          </a>
        </Button>
      </div>

      {/* Usage pricing */}
      <div className="mt-4 rounded-card bg-card p-6 sm:p-8 md:p-10">
        <h3 className="text-[22px] font-medium tracking-tight">Usage pricing</h3>
        <p className="text-[15px] text-muted-foreground mt-2">Charged per request from your wallet, on the token counts the provider reports.</p>
        <dl className="mt-6 grid gap-x-8 md:grid-cols-2">
          {usage.map((u) => (
            <div key={u.title} className="py-4 border-t border-border/60">
              <dt className="text-[15px] font-medium">{u.title}</dt>
              <dd className="text-[14px] text-muted-foreground mt-1 leading-relaxed">{u.body}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-6 pt-6 border-t border-border/60 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[15px] text-muted-foreground">Credit doesn't expire. At $0, model calls stop until you add credit.</p>
          <Button variant="brand" className="rounded-full h-11 pl-6 pr-5 gap-2" onClick={onStart}>
            {startLabel}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
